import Config from 'config/settings';
import { DOMHelper } from 'core/DOMHelper';
import { SectionType } from 'app/data/Enums';
import { HistoryStateModel } from 'app/model/HistoryStateModel';
import { device } from 'utils/device';

/**
 * DOM side of the nav menu: element refs, the open/close menu animation
 * (stagger tweens + the delayed close button), the exit-link blackout, the
 * selected-link states and the body/html about-scroll-lock classes. Event
 * wiring stays in NavMenuController (Events.on registrations live there).
 */
export class NavMenuView {
	private _dom: DOMHelper;
	private _container: Element;

	public $menuButton: HTMLElement;
	public $background: HTMLElement;
	public $innerBackground: HTMLElement;
	public $backButton: HTMLElement;
	public $contact: HTMLElement;
	public $content: HTMLElement;
	public $close: HTMLElement;

	private _staggerInMenuTween: gsap.Tween;
	private _staggerInMenuTweenContact: gsap.Tween;
	//NOTE: dead state moved intact from NavMenuController — never assigned anywhere, only cleared in reset()
	private _setStateTimeout: number;

	private _needsHardMenuHide: boolean = false;

	private _closeButtonShowDelayTimeout;


	constructor(dom: DOMHelper) {
		this._dom = dom;
	}

	//Element refs
	//-------------------------------------------------------------------------------------

	public queryRefs(container: Element): void {
		this._container = container;

		this.$menuButton = document.querySelector('.show__info__button');
		this.$innerBackground = container.querySelector('.navmenu__inner__background');
		this.$background = container.querySelector('.navmenu__background');
		this.$backButton = container.querySelector('.navmenu__back');
		this.$contact = container.querySelector('.navmenu__contact');
		this.$content = container.querySelector('.navmenu__content');
		this.$close = document.querySelector('.ui_close__button');
	}

	//Exit-link blackout
	//-------------------------------------------------------------------------------------

	public exitLink(e: MouseEvent): void {
		e.preventDefault();
		gsap.TweenMax.to(this._container, 0.2, { scale: 1.05 });
		let $blackout = document.querySelector('.navmenu__blackout');
		this._dom.addClass($blackout, '-show');
		gsap.TweenMax.fromTo(
			$blackout,
			0.2,
			{ opacity: 0 },
			{
				autoAlpha: 1,
				ease: Config.linearEase,
				onComplete: () => {
					window.location.href = (e.target as HTMLAnchorElement).href;
				}
			}
		);
	}

	//Selected-link states
	//-------------------------------------------------------------------------------------

	//** Select the selected state of menu */
	public setMenuSelectedState(state: HistoryStateModel = null): void {
		this._removeAllSelectClassesFromMenu();
		let $target: HTMLElement;

		if (
			state &&
			(state.section === SectionType.WORK || state.section === SectionType.ABOUT || state.section === SectionType.HOME)
		) {
			if (state.section === SectionType.HOME)
				$target = this._container.querySelector(`.navmenu__link.-` + state.section);
			else $target = this._container.querySelector(`.navmenu__link.-` + state.subsection);

			this._dom.addClass($target, '-selected');
		}
	}

	/** Remove all selected states */
	private _removeAllSelectClassesFromMenu(): void {
		this._container.querySelectorAll('.navmenu__link').forEach((link: Element) => {
			this._dom.removeClass(link, '-selected');
		});
	}

	public openLinksState(openingState: HistoryStateModel): void {
		if (openingState.section === SectionType.WORK) {
			let $target = this._container.querySelector(`.navmenu__link.-work`);
			this._dom.addClass($target, '-selected');
		}
	}

	//Open / close animation
	//-------------------------------------------------------------------------------------

	public resetMenu(): void {
		gsap.TweenMax.set(this.$backButton, { autoAlpha: 0 });
		gsap.TweenMax.set(this.$contact, { autoAlpha: 1 });
	}

	/** The open classes + hard-set for the direct-to-about (deeplink/history) open — about calls stay in the controller */
	public setMenuToAboutState(): void {
		this._dom.addClass(this.$menuButton, '-open');
		this._dom.addClass(this._container, '-open');
		this._dom.addClass(this._container, '-active');

		gsap.TweenMax.set(this._container, { scale: 1, autoAlpha: 1 });
	}

	public prepareAnimateMenu(): void {
		if (device.isPortraitDevice) {
		} else {
		}
	}

	public animateInMenu(): void {
		this._dom.addClass(this.$menuButton, '-open');
		this._dom.addClass(this._container, '-open');
		this._dom.addClass(this._container, '-active');
		this.delayCloseButtonShow();

		//Reset the content panel from any animations

		if (device.isPortraitDevice) {
		} else {
		}

		//Show the menu items
		this.showMenuContent();
	}

	public reset(): void {
		clearTimeout(this._setStateTimeout);

		if (this._staggerInMenuTween) {
			this._staggerInMenuTween.kill();
		}

		if (this._staggerInMenuTweenContact) {
			this._staggerInMenuTweenContact.kill();
		}
	}

	public animateOutMenu(delay: number = 0): void {
		this.reset();
		this._resetCloseButtonDelay();

		this._dom.removeClass(this.$menuButton, '-open');
		setTimeout(() => this._dom.removeClass(this._container, '-active'), 350);
		this._hideMenuContent();
		setTimeout(() => this._dom.removeClass(this._container, '-open'), 0);

		if (this._needsHardMenuHide) {
			gsap.TweenMax.set('.navmenu__link', { autoAlpha: 0 });
		}

		this.blockAboutScroll();
	}

	/** Show menu content */
	public showMenuContent(showContact: boolean = true): void {
		this._needsHardMenuHide = true;

		this._staggerInMenuTween = gsap.TweenMax.staggerFromTo(
			'.navmenu__link',
			0.3,
			{ x: 80, autoAlpha: 0 },
			{
				x: 0,
				autoAlpha: 1,
				delay: 0.3,
				onComplete: () => {
					this._needsHardMenuHide = false;
				}
			},
			0.04
		);

		if (showContact) {
			this._staggerInMenuTweenContact = gsap.TweenMax.fromTo(
				'.navmenu__contact',
				0.3,
				{ y: 0, autoAlpha: 0, x: 80 },
				{ y: 0, x: 0, autoAlpha: 1, delay: 0.5 }
			);
		}
	}

	/** Hide menu content */
	private _hideMenuContent(): void {
		if (this._staggerInMenuTweenContact) {
			this._staggerInMenuTweenContact = gsap.TweenMax.to('.navmenu__contact', 0.15, { y: 0, autoAlpha: 0 });
		}
	}

	//Close button delay
	//-------------------------------------------------------------------------------------

	public delayCloseButtonShow(): void {
		if (this._closeButtonShowDelayTimeout) {
			clearTimeout(this._closeButtonShowDelayTimeout);
		}

		this._dom.addClass(this.$close, '-animatingtomenu');

		this._closeButtonShowDelayTimeout = setTimeout(() => {
			this._closeButtonShowDelayTimeout = null;
			this._resetCloseButtonDelay();
		}, 1000);
	}

	private _resetCloseButtonDelay(): void {
		if (this._closeButtonShowDelayTimeout) {
			clearTimeout(this._closeButtonShowDelayTimeout);
		}

		this._closeButtonShowDelayTimeout = null;
		this._dom.removeClass(this.$close, '-animatingtomenu');
	}

	//About scroll-lock body classes
	//-------------------------------------------------------------------------------------

	public allowAboutScroll(): void {
		this._dom.addClass(document.querySelector('body'), '-aboutactive');
		this._dom.addClass(document.querySelector('html'), '-aboutactive');
	}

	public blockAboutScroll(): void {
		this._dom.removeClass(document.querySelector('body'), '-aboutactive');
		this._dom.removeClass(document.querySelector('html'), '-aboutactive');
	}
}
